import { useState, useRef, useEffect } from 'react';
import formatMessage from 'format-message';

/**
 * Hook to handle debounced search with timeout
 */
export const useSearchDebounce = (onSearch, onReset, delay = 500) => {
  const [inputValue, setInputValue] = useState('');
  const searchTimeoutRef = useRef(null);

  const handleInputChange = (value) => {
    setInputValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (value === '' || value.length < 3) {
      if (onReset) {
        onReset();
      }
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (onSearch) {
        onSearch(value);
      }
    }, delay);
  };

  const resetInputValue = () => {
    setInputValue('');
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return { inputValue, handleInputChange, resetInputValue };
};

/**
 * Hook to manage select options state (showing, highlighting, etc.)
 */
export const useSelectOptionsState = (availableOptions) => {
  const [isShowingOptions, setIsShowingOptions] = useState(false);
  const [highlightedOptionId, setHighlightedOptionId] = useState(null);

  useEffect(() => {
    if (availableOptions.length > 0 && !highlightedOptionId) {
      setHighlightedOptionId(availableOptions[0].id);
    }
  }, [availableOptions, highlightedOptionId]);

  const showOptions = () => setIsShowingOptions(true);
  const hideOptions = () => {
    setIsShowingOptions(false);
    setHighlightedOptionId(null);
  };

  const highlightOption = (optionId) => {
    setHighlightedOptionId(optionId);
  };

  const resetHighlight = () => {
    setHighlightedOptionId(null);
  };

  return {
    isShowingOptions,
    highlightedOptionId,
    showOptions,
    hideOptions,
    highlightOption,
    resetHighlight,
    setIsShowingOptions,
    setHighlightedOptionId
  };
};

/**
 * Hook to transform curators and search results into option format
 */
export const useCuratorOptions = (selectedCurators, searchResults) => {
  const selectedOptions = selectedCurators.map(curator => {
    const curatorName = `${curator.firstName || ''} ${curator.lastName || ''}`.trim();
    return {
      id: curator.uuid,
      label: `${curatorName} ${curator.email}`.trim()
    };
  });

  const selectedCuratorIds = selectedCurators.map(c => c.uuid);

  const availableOptions = searchResults
    .filter(user => !selectedCuratorIds.includes(user.uuid))
    .map(user => ({
      id: user.uuid,
      label: `${user.fullName} ${user.email}`.trim()
    }));

  const getOptionById = (queryId) => {
    return availableOptions.find(({ id }) => id === queryId);
  };

  return {
    selectedOptions,
    availableOptions,
    getOptionById
  };
};

/**
 * Hook to manage accessibility announcements
 */
export const useAnnouncements = (availableOptions, highlightedOptionId, getOptionById, isSearching, inputValue) => {
  const [announcement, setAnnouncement] = useState(null);

  const getOptionsChangedMessage = (newOptions) => {
    let message =
      newOptions.length !== availableOptions.length
        ? formatMessage('{count, plural, one {# option available} other {# options available}}.', { count: newOptions.length })
        : null;

    if (message && newOptions.length > 0) {
      if (highlightedOptionId !== newOptions[0].id) {
        const option = getOptionById(newOptions[0].id);
        if (option) {
          message = formatMessage('{label}. {message}', { label: option.label, message });
        }
      }
    }
    return message;
  };

  useEffect(() => {
    if (!isSearching && availableOptions.length > 0 && inputValue.length >= 3) {
      const message = getOptionsChangedMessage(availableOptions);
      if (message) {
        setAnnouncement(message);
      }
    }
  }, [isSearching, availableOptions.length, highlightedOptionId, getOptionById, inputValue]);

  return { announcement, setAnnouncement };
};
